import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BriefingSection from "../components/BriefingSection";
import { api } from "../lib/api";
import { sortSectionsForDisplay } from "../lib/sectionOrder";
import { BarChart3, Calendar, Target, Building2 } from "lucide-react";

const GOAL_LABELS = {
  full_audit: "Full Audit",
  grow_revenue: "Grow Revenue",
  cut_waste: "Cut Waste",
  understand_history: "Understand History",
};

export default function SharePage() {
  const { token } = useParams();
  const [briefing, setBriefing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setBriefing(await api.getShared(token));
      } catch (e) {
        setError(e?.response?.data?.detail || "Link not found or expired.");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center">
        <div>
          <p className="font-heading text-2xl font-medium text-slate-900">{error}</p>
          <p className="mt-2 text-sm text-slate-500">Ask the sender for a fresh link.</p>
        </div>
      </div>
    );
  }
  if (!briefing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-1.5 text-slate-500">
          <span className="loading-dot">·</span>
          <span className="loading-dot">·</span>
          <span className="loading-dot">·</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0052FF] text-white">
              <BarChart3 size={18} strokeWidth={1.8} />
            </div>
            <div>
              <p className="font-heading text-sm font-medium text-slate-900">Handoff · Shared briefing</p>
              <p className="text-[11px] text-slate-400">Read-only link · no account access</p>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-12 md:px-10">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">shared briefing</p>
        <h1 className="mt-2 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
          {briefing.customer_name || "Account"}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <Building2 size={12} strokeWidth={1.8} />
            <span className="num">{briefing.customer_id}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={12} strokeWidth={1.8} />
            Last {briefing.lookback_days} days
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Target size={12} strokeWidth={1.8} />
            {GOAL_LABELS[briefing.goal] || briefing.goal}
          </span>
          <span>Generated {new Date(briefing.created_at).toLocaleString()}</span>
        </div>

        <div className="mt-10 space-y-3">
          {sortSectionsForDisplay(briefing.sections || []).map((section, idx) => (
            <BriefingSection
              key={section.key}
              section={section}
              index={idx}
              defaultOpen={idx === 0}
              readOnly
            />
          ))}
        </div>
      </div>
    </div>
  );
}
